import {
  useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet,
  useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet,
  type WastewaterPCRActivityLevel,
} from "@phc/common/gen/threatAnalyticsGen"
import type { Dayjs } from "dayjs"
import { useMemo } from "react"
import { makeAnomalyKey } from "./WastewaterUtils"

/**
 * Fetches both the activity-level and PCR-analysis endpoints and derives
 * the commonly-needed `anomalyKeys` set and `activityLevelMap` lookup.
 */
export const useWastewaterData = ({
  phcSiteId,
  limit,
  startDate,
  endDate,
}: {
  phcSiteId: string
  limit: number
  startDate?: Dayjs | null
  endDate?: Dayjs | null
}) => {
  const {
    data: activityLevelResponse,
    isLoading: isActivityLoading,
    isError: isActivityError,
    refetch: refetchActivityLevel,
  } = useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet({
    limit,
    phc_site_id: phcSiteId,
    date_range_start: startDate?.toISOString(),
    date_range_end: endDate?.toISOString(),
  })

  const {
    data: pcrAnalysisData,
    isLoading: isAnalysisLoading,
    isError: isAnalysisError,
    refetch: refetchAnalysis,
  } = useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet({
    limit,
    phc_site_id: phcSiteId,
    date_range_start: startDate?.toISOString(),
    date_range_end: endDate?.toISOString(),
  })

  const activityLevels: WastewaterPCRActivityLevel[] = useMemo(
    () => activityLevelResponse ?? [],
    [activityLevelResponse]
  )

  const anomalyKeys = useMemo(() => {
    const keys = new Set<string>()
    if (!pcrAnalysisData) return keys
    for (const item of pcrAnalysisData) {
      const isAnomalous =
        item.surge_flag === "anomalous" ||
        item.spike_flag === "anomalous" ||
        item.rare_flag === "anomalous"
      if (isAnomalous) {
        keys.add(makeAnomalyKey(item.target_name, item.sampling_end_date))
      }
    }
    return keys
  }, [pcrAnalysisData])

  const activityLevelMap = useMemo(() => {
    const map = new Map<string, WastewaterPCRActivityLevel>()
    for (const item of activityLevels) {
      map.set(makeAnomalyKey(item.target_name, item.sampling_end_date), item)
    }
    return map
  }, [activityLevels])

  return {
    /** Typed array of activity-level records from the new endpoint. */
    activityLevels,
    /** Raw response from the old PCR-analysis endpoint (for anomaly_flag, etc). */
    pcrAnalysisData,
    /** Set of "targetName__samplingEndDate" keys that are anomalous. */
    anomalyKeys,
    /** Map from anomaly key → activity-level record for level lookups. */
    activityLevelMap,
    refetch: async () => {
      await Promise.all([refetchActivityLevel(), refetchAnalysis()])
    },
    isLoading: isActivityLoading || isAnalysisLoading,
    isError: isActivityError || isAnalysisError,
  }
}
